import { Alert, AppBar, Box, Button, CircularProgress, Paper, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../services/HttpService';
import { AppDevice, newAppDevice, AppDeviceMessage, newAppDeviceMessage, AppBase } from '../../../model/AppBase';
import { AppDeviceForm, AppDeviceFormRef } from '../../../model/forms/AppDeviceForm';
import { TabPanel, TabProps } from '../../../components/ui/TabPanel';
import { DataInfoTable, DataListInfoTable } from '../../../components/tables/DataTable';
import { AppDeviceMessageForm, AppDeviceMessageFormRef } from '../../../model/forms/AppDeviceMessageForm';
import { PrepaymentSolutionForm, PrepaymentSolutionFormRef } from '../../../model/forms/PrepaymentSolutionForm';

interface AppDeviceOverviewProps {
  app?: AppBase;
  item: AppDevice;
  getError?: string;
  onClose?: () => void;
//   AppDeviceMessages?: AppDeviceMessage[];
  onItemChange?: (item: AppDevice) => void;
}

export const AppDeviceOverview = (props: AppDeviceOverviewProps) => {
  const theme = useTheme();
  const { getError, onItemChange} = props;
  const { api, error, isLoading } = useApi();
  const { api: modApi, error: modError, isLoading: modIsLoading } = useApi();
  const addAppDeviceRef = useRef<AppDeviceFormRef>(null);
  const addPrePaymentRef = useRef<PrepaymentSolutionFormRef>(null);
  const addErpRef = useRef<AppDeviceMessageFormRef>(null);
  const [item, setItem] = useState<AppDevice>(props.item);
  const [AppDeviceMessage, setAppDeviceMessage] = useState<AppDeviceMessage>(newAppDeviceMessage());
  const [displayData, setDisplayData] = useState<Record<string, string>>(null);
  const [displayDataPrepayment, setDisplayDataPrepayment] = useState<Record<string, string>>(null);
  const [displayDataErp, setDisplayDataErp] = useState<Record<string, string>>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [AppDeviceMessageList, setAppDeviceMessageList] = useState<AppDeviceMessage[]>([]);
  const [SelectedAppDeviceMessage, setSelectedAppDeviceMessage] = useState<AppDeviceMessage>(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!props.item || props.item.id === 0) {
      setTabIndex(1);
    } else {
      setDisplayData({
        "ID": String(props.item.id),
        "Name": props.item.name,
        "Device": String(props.item.device_id),
        "Company": String(props.item.company_id),
        "Vessel": String(props.item.vehicle_id),
        "Initiated": props.item.app_id ? "Yes" : "No",
        "Last seen": props.item.last_seen_at? props.item.last_seen_at : "",
        "Messages": props.item.messages? String(props.item.messages[0].name) : "",
        "Action": "",
      })
    }
  }, [props.item]);

  const reset = () => {
    setItem(() => ({ ...item, password: "" })); // triggers props.AppDevice change in AppDeviceForm
  }

  const saveAppDevice = () => {
    if (!addAppDeviceRef.current) return;

    // Validate the form
    if (!addAppDeviceRef.current.valid()) {
      console.log("Validation failed");
      return;
    }
  
    // Get form data
    const AppDeviceRef = addAppDeviceRef.current.getItem();
    console.log("Saving data:", AppDeviceRef);

    if (addAppDeviceRef.current.valid()) {
      api("/manage/app/AppDevice", "POST", { ...AppDeviceRef }).then(res => {
        console.log("res:", res)
        if (res?.statusCode === 200) {
          if (res.AppDevice){
            onItemChange(res.AppDevice);
          }
          setTabIndex(0);
        }
      });
    }
  };

  const savePrepayment = () => {
    if (!addPrePaymentRef.current){
      alert("Not current");
        return;
    }
    // Validate the form
    if (!addPrePaymentRef.current.valid()) {
      console.log("Validation failed");
      alert("Not valid");
      return;
    }
  
    // Get form data
    const formData = addPrePaymentRef.current.getPrepaymentSolution();
    console.log("Saving data:", formData);

    if (addPrePaymentRef.current.valid()) {
      api("/manage/order/prepaymentsolution", "POST", { ...formData }).then(res => {
        console.log("res:", res)
        if (res?.statusCode === 200) {
          // onItemChange(res.payment_profile);
          setTabIndex(0);
        }
      });
    }
  };

  const saveErp = () => {
    if (!addErpRef.current) return;
  
    // Validate the form
    if (!addErpRef.current.valid()) {
      console.log("Validation failed");
      return;
    }
  
    // Get form data
    const formData = addErpRef.current.getItem();
    console.log("Saving data:", formData);

    if (addErpRef.current.valid()) {
      api("/manage/order/AppDeviceMessage", "POST", { ...formData }).then(res => {
        console.log("res:", res)
        if (res?.statusCode === 200) {
          // onItemChange(res.payment_profile);
          setTabIndex(0);
        }
      });
    }
  };

  const changeTab = (index: number, performChange: boolean = true): boolean => {
    if (!item.id) {
      if (performChange) {
        setTabIndex(1);
      }

      return index === 1
    }

    if (performChange) {
      setTabIndex(index);
    }

    return true;
  }

  return (
    <>
      <AppBar position="static"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? "inherit" : theme.palette.grey[300],
          color: theme.palette.mode === 'dark' ? "white" : "black"
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant={"scrollable"}
          textColor="inherit"
          indicatorColor="primary"
          aria-label="manage AppDevice tabs"
        >
          {[
            "Overview",
            item.id ? "Edit" : "Create",
            // "Prepayment solution",
            // "Erp solution",
          ].map((name, index) => (
            <Tab disabled={!changeTab(index, false)}  key={index} label={name} sx={{ fontWeight: "bold", textTransform: "none" }} {...TabProps(index)} />
          ))}
        </Tabs>
      </AppBar>
      <Box sx={{ p: { xs: 1, md: 3 }, pt: 3, minHeight: 400 }}>
        <TabPanel value={tabIndex} index={0} sx={{ minHeight: 200 }}>
          <Box sx={{ p: { xs: 1, md: 3 }, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: { xs: "100%", md: 1200 } }}>
              <DataInfoTable title={"Device"} data={displayData} onEdit={() => changeTab(1)} />
              {props.onClose && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', p: 2 }}>
                  <Button variant='contained' color='darkgrey' onClick={props.onClose}>Close</Button>
                </Box>
              )}
            </Box>
          </Box>
        </TabPanel>
        <TabPanel value={tabIndex} index={1} sx={{ minHeight: 200 }}>
          <Box sx={{ p: { xs: 1, md: 3 }, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: 1200 }}>
              <AppDeviceForm item={item} messages={AppDeviceMessageList} error={getError} ref={addAppDeviceRef} />
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* <Button variant='contained' color='info' onClick={setDefault}>Default</Button> */}
                  <Button variant='contained' color='darkgrey' onClick={reset}>Reset</Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Button
                    variant='contained'
                    color='darkgrey'
                    onClick={() => {
                      if (changeTab(0, false)) {
                        changeTab(0);
                      } else if (props.onClose) {
                        props.onClose();
                      }
                    }}
                  >
                    Abort
                  </Button>
                  <Button
                    variant='contained'
                    color='info'
                    onClick={saveAppDevice}
                    disabled={isLoading}
                    endIcon={isLoading && <CircularProgress size={22} />}
                  >
                    {item.id ? "Save" : "Create"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>
      </Box>
    </>
  )
}